/**
 * MessagingNumbersView — admin-only management UI for the 10DLC long-code
 * pool driving outbound SMS through the tenant's Twilio Messaging Service.
 *
 * Sections (top-down):
 *   1. Access guard (red card if not ADMIN — matches DataChangesView pattern)
 *   2. ViewHeader with title / subtitle / InfoTip / "Buy new number" action
 *   3. Stats summary row (active count, 30d sent, weighted delivered %, weighted opt-out %)
 *   4. Health-alert callouts — one yellow card per number breaching delivery /
 *      opt-out thresholds; clicking scrolls to the row in the table
 *   5. Numbers table (sortable, expandable rows, kebab actions)
 *   6. Buy-new modal (Search → Confirm two-step)
 *   7. Edit-notes modal
 *   8. Release-confirmation modal (requires typed last-4-digit match)
 *
 * Pattern: plain axios + useState + useEffect with a `reloadCounter` bump
 * after mutations (same shape DataChangesView uses) — NOT React Query.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Phone,
  Plus,
  AlertTriangle,
  MoreVertical,
  ChevronDown,
  ChevronRight,
  X,
  Search,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import {
  listMessagingNumbers,
  searchAvailableNumbers,
  buyMessagingNumber,
  updateMessagingNumber,
  releaseMessagingNumber,
} from '@/api/messagingNumbers';
import type {
  MessagingNumber,
  MessagingNumberStatus,
  MessagingNumberSearchResult,
} from '@/types/messaging';
import { ViewHeader } from '@/components/ViewHeader';
import InfoTip from '@/components/InfoTip';
import EmptyState from '@/components/EmptyState';
import { Skeleton } from '@/components/Skeleton';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format E.164 (+18165550123) → "+1 (816) 555-0123". Falls back to input. */
function formatPhone(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return e164;
}

/** Last 4 digits of an E.164 number — used for the release-confirm guard. */
function last4(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  return digits.slice(-4);
}

function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** "4 min ago" style relative timestamp. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  const diffYr = Math.round(diffMo / 12);
  return `${diffYr}y ago`;
}

function formatPct(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined) return '—';
  return `${(fraction * 100).toFixed(1)}%`;
}

const STATUS_PILL: Record<MessagingNumberStatus, string> = {
  active: 'bg-status-success-bg/30 text-status-success border-status-success-bg/50',
  paused: 'bg-status-warning-bg/30 text-status-warning border-status-warning-bg/50',
  draining: 'bg-bg-elevated text-fg-secondary border-border-base',
  released: 'bg-status-danger-bg/30 text-status-danger border-status-danger-bg/50',
};

function StatusPill({ status }: { status: MessagingNumberStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_PILL[status]}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

type SortKey =
  | 'phone_number'
  | 'status'
  | 'sent_30d'
  | 'delivered_rate_30d'
  | 'opt_out_rate_30d'
  | 'last_used_at';

interface SortState {
  key: SortKey;
  order: 'asc' | 'desc';
}

function compareNumbers(
  a: MessagingNumber,
  b: MessagingNumber,
  sort: SortState,
): number {
  const dir = sort.order === 'asc' ? 1 : -1;
  switch (sort.key) {
    case 'phone_number':
      return a.phone_number.localeCompare(b.phone_number) * dir;
    case 'status':
      return a.status.localeCompare(b.status) * dir;
    case 'sent_30d':
      return ((a.sent_30d ?? 0) - (b.sent_30d ?? 0)) * dir;
    case 'delivered_rate_30d':
      return ((a.delivered_rate_30d ?? 0) - (b.delivered_rate_30d ?? 0)) * dir;
    case 'opt_out_rate_30d':
      return ((a.opt_out_rate_30d ?? 0) - (b.opt_out_rate_30d ?? 0)) * dir;
    case 'last_used_at': {
      const at = a.last_used_at ? new Date(a.last_used_at).getTime() : 0;
      const bt = b.last_used_at ? new Date(b.last_used_at).getTime() : 0;
      return (at - bt) * dir;
    }
    default:
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Toast — lightweight in-component toast (no global dependency)
// ---------------------------------------------------------------------------

interface ToastState {
  kind: 'success' | 'error';
  message: string;
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export interface MessagingNumbersViewProps {
  userRole?: string;
  onBack?: () => void;
}

export function MessagingNumbersView({
  userRole,
  onBack,
}: MessagingNumbersViewProps) {
  // ============================================================================
  // Access guard — defense in depth (matches DataChangesView)
  // ============================================================================
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  if (roleNorm !== 'ADMIN') {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">
            Access Denied
          </h2>
          <p className="text-fg-secondary">
            The 10DLC Number Pool is available to administrators only. If you
            believe this is a mistake, contact your tenant admin.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // State
  // ============================================================================
  const [numbers, setNumbers] = useState<MessagingNumber[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [toast, setToast] = useState<ToastState | null>(null);

  const [sort, setSort] = useState<SortState>({
    key: 'sent_30d',
    order: 'desc',
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [openKebabId, setOpenKebabId] = useState<number | null>(null);

  const [buyOpen, setBuyOpen] = useState(false);
  const [editNotesFor, setEditNotesFor] = useState<MessagingNumber | null>(
    null,
  );
  const [releaseFor, setReleaseFor] = useState<MessagingNumber | null>(null);

  // Row refs so health-alert callouts can scroll to a specific number.
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const setRowRef = useCallback(
    (id: number) => (el: HTMLTableRowElement | null) => {
      if (el) rowRefs.current.set(id, el);
      else rowRefs.current.delete(id);
    },
    [],
  );

  // ============================================================================
  // Toast auto-dismiss
  // ============================================================================
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ============================================================================
  // Close kebab when clicking outside
  // ============================================================================
  useEffect(() => {
    if (openKebabId === null) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-kebab-root]')) setOpenKebabId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openKebabId]);

  // ============================================================================
  // Load
  // ============================================================================
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listMessagingNumbers()
      .then((data) => {
        if (cancelled) return;
        setNumbers(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message ?? 'Failed to load messaging numbers');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadCounter]);

  const refresh = useCallback(() => setReloadCounter((c) => c + 1), []);

  // ============================================================================
  // Derived: sorted rows + summary stats + health alerts
  // ============================================================================
  const sortedRows = useMemo(() => {
    if (!numbers) return [];
    return [...numbers].sort((a, b) => compareNumbers(a, b, sort));
  }, [numbers, sort]);

  const summary = useMemo(() => {
    if (!numbers || numbers.length === 0) {
      return {
        activeCount: 0,
        totalSent: 0,
        weightedDeliveredRate: null as number | null,
        weightedOptOutRate: null as number | null,
      };
    }
    const activeCount = numbers.filter((n) => n.status === 'active').length;
    const totalSent = numbers.reduce((s, n) => s + (n.sent_30d ?? 0), 0);
    let weightedDelivered = 0;
    let weightedOptOut = 0;
    let denom = 0;
    for (const n of numbers) {
      const sent = n.sent_30d ?? 0;
      if (sent <= 0) continue;
      if (n.delivered_rate_30d != null) {
        weightedDelivered += n.delivered_rate_30d * sent;
      }
      if (n.opt_out_rate_30d != null) {
        weightedOptOut += n.opt_out_rate_30d * sent;
      }
      denom += sent;
    }
    return {
      activeCount,
      totalSent,
      weightedDeliveredRate: denom > 0 ? weightedDelivered / denom : null,
      weightedOptOutRate: denom > 0 ? weightedOptOut / denom : null,
    };
  }, [numbers]);

  const healthAlerts = useMemo(() => {
    if (!numbers) return [];
    const alerts: Array<{
      number: MessagingNumber;
      kind: 'opt_out' | 'delivered';
      message: string;
    }> = [];
    for (const n of numbers) {
      if (n.status === 'released') continue;
      if ((n.opt_out_rate_30d ?? 0) > 0.03) {
        alerts.push({
          number: n,
          kind: 'opt_out',
          message: `Number ${formatPhone(n.phone_number)} has elevated opt-out rate (${formatPct(n.opt_out_rate_30d)}). Consider pausing and routing to a fresh number.`,
        });
      }
      if (
        n.delivered_rate_30d != null &&
        n.delivered_rate_30d < 0.85 &&
        (n.sent_30d ?? 0) > 0
      ) {
        alerts.push({
          number: n,
          kind: 'delivered',
          message: `Number ${formatPhone(n.phone_number)} delivery rate dropped to ${formatPct(n.delivered_rate_30d)}. Carrier may have flagged it.`,
        });
      }
    }
    return alerts;
  }, [numbers]);

  // ============================================================================
  // Action handlers
  // ============================================================================
  const handleTogglePause = useCallback(
    async (n: MessagingNumber) => {
      try {
        const nextStatus: MessagingNumberStatus =
          n.status === 'active' ? 'paused' : 'active';
        await updateMessagingNumber(n.id, { status: nextStatus });
        setToast({
          kind: 'success',
          message: `${formatPhone(n.phone_number)} ${nextStatus === 'paused' ? 'paused' : 'resumed'}.`,
        });
        refresh();
      } catch (err) {
        setToast({
          kind: 'error',
          message:
            (err as Error)?.message ?? 'Failed to update number status.',
        });
      } finally {
        setOpenKebabId(null);
      }
    },
    [refresh],
  );

  const scrollToRow = useCallback((id: number) => {
    const el = rowRefs.current.get(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-status-warning');
      window.setTimeout(() => {
        el.classList.remove('ring-2', 'ring-status-warning');
      }, 2000);
    }
  }, []);

  const setSortKey = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, order: prev.order === 'desc' ? 'asc' : 'desc' }
        : { key, order: 'desc' },
    );
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="min-h-dvh bg-bg-base text-fg-primary">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg-primary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}

        <ViewHeader
          title="10DLC Number Pool"
          tagline="Phone numbers for SMS outreach"
          icon={<Phone className="w-6 h-6" />}
          inline={
            <InfoTip
              term={{
                title: '10DLC + Messaging Service',
                short:
                  '10DLC = 10-Digit Long Code — your registered SMS sender numbers. Twilio rotates among the pool for outbound; recipients see a consistent number per thread.',
              }}
            />
          }
          actions={
            <button
              type="button"
              onClick={() => setBuyOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent hover:bg-accent-hover text-white font-semibold text-sm px-3 py-2 min-h-11 sm:min-h-0 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Buy new number
            </button>
          }
        />

        {/* Summary cards */}
        <SummaryCards
          activeCount={summary.activeCount}
          totalSent={summary.totalSent}
          deliveredRate={summary.weightedDeliveredRate}
          optOutRate={summary.weightedOptOutRate}
          loading={loading && !numbers}
        />

        {/* Health alerts */}
        {healthAlerts.length > 0 && (
          <div className="space-y-2">
            {healthAlerts.map((a, i) => (
              <button
                key={`${a.number.id}-${a.kind}-${i}`}
                type="button"
                onClick={() => scrollToRow(a.number.id)}
                className="w-full text-left flex items-start gap-3 rounded-lg border border-status-warning-bg/40 bg-status-warning-bg/15 px-4 py-3 hover:bg-status-warning-bg/25 transition-colors"
              >
                <AlertTriangle
                  className="w-5 h-5 text-status-warning shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <p className="text-sm text-fg-primary">{a.message}</p>
              </button>
            ))}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="rounded-lg border border-status-danger-bg/40 bg-status-danger-bg/20 p-4">
            <p className="text-status-danger font-semibold">
              Failed to load numbers
            </p>
            <p className="text-status-danger text-sm mt-1">{error}</p>
            <button
              type="button"
              onClick={refresh}
              className="mt-2 px-3 py-1.5 bg-accent hover:bg-accent-hover text-white text-sm font-semibold rounded"
            >
              Retry
            </button>
          </div>
        )}

        {/* Numbers table */}
        {loading && !numbers ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : !numbers || numbers.length === 0 ? (
          <EmptyState
            icon={Phone}
            title="No numbers in your pool yet"
            description="Buy your first 10DLC long code to start sending SMS."
            action={{
              label: 'Buy new number',
              onClick: () => setBuyOpen(true),
            }}
          />
        ) : (
          <NumbersTable
            rows={sortedRows}
            sort={sort}
            onSort={setSortKey}
            expandedId={expandedId}
            onToggleExpand={(id) =>
              setExpandedId((cur) => (cur === id ? null : id))
            }
            openKebabId={openKebabId}
            onOpenKebab={setOpenKebabId}
            onTogglePause={handleTogglePause}
            onEditNotes={(n) => {
              setEditNotesFor(n);
              setOpenKebabId(null);
            }}
            onRelease={(n) => {
              setReleaseFor(n);
              setOpenKebabId(null);
            }}
            setRowRef={setRowRef}
          />
        )}
      </div>

      {/* Buy-new modal */}
      {buyOpen && (
        <BuyNumberModal
          onClose={() => setBuyOpen(false)}
          onPurchased={(purchased) => {
            setBuyOpen(false);
            refresh();
            setToast({
              kind: 'success',
              message: `Purchased ${formatPhone(purchased.phone_number)}.`,
            });
          }}
        />
      )}

      {/* Edit-notes modal */}
      {editNotesFor && (
        <EditNotesModal
          number={editNotesFor}
          onClose={() => setEditNotesFor(null)}
          onSaved={() => {
            setEditNotesFor(null);
            refresh();
            setToast({ kind: 'success', message: 'Notes saved.' });
          }}
          onError={(msg) => setToast({ kind: 'error', message: msg })}
        />
      )}

      {/* Release-confirm modal */}
      {releaseFor && (
        <ReleaseModal
          number={releaseFor}
          onClose={() => setReleaseFor(null)}
          onReleased={() => {
            setReleaseFor(null);
            refresh();
            setToast({
              kind: 'success',
              message: 'Number released back to Twilio.',
            });
          }}
          onError={(msg) => setToast({ kind: 'error', message: msg })}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-6 right-6 z-[10000] rounded-lg shadow-2xl px-4 py-3 text-sm font-medium border ${
            toast.kind === 'success'
              ? 'bg-status-success-bg/30 border-status-success-bg/50 text-status-success'
              : 'bg-status-danger-bg/30 border-status-danger-bg/50 text-status-danger'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: SummaryCards
// ---------------------------------------------------------------------------

interface SummaryCardsProps {
  activeCount: number;
  totalSent: number;
  deliveredRate: number | null;
  optOutRate: number | null;
  loading: boolean;
}

function SummaryCards({
  activeCount,
  totalSent,
  deliveredRate,
  optOutRate,
  loading,
}: SummaryCardsProps) {
  const cards: Array<{
    label: string;
    value: string;
    hint?: string;
    tone?: 'default' | 'warning' | 'danger';
  }> = [
    { label: 'Active numbers', value: loading ? '—' : String(activeCount) },
    {
      label: 'Total sent (30d)',
      value: loading ? '—' : totalSent.toLocaleString(),
    },
    {
      label: 'Delivered rate (pool avg)',
      value: loading ? '—' : formatPct(deliveredRate),
      tone:
        !loading && deliveredRate != null && deliveredRate < 0.9
          ? 'warning'
          : 'default',
      hint: 'Sent-weighted across the pool over 30d.',
    },
    {
      label: 'Opt-out rate (pool avg)',
      value: loading ? '—' : formatPct(optOutRate),
      tone:
        !loading && optOutRate != null && optOutRate > 0.03
          ? 'danger'
          : 'default',
      hint: 'Sent-weighted across the pool over 30d.',
    },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-lg border border-border-base bg-bg-raised p-4"
        >
          <div className="text-xs uppercase tracking-wide text-fg-muted font-semibold">
            {c.label}
          </div>
          <div
            className={`mt-1.5 text-2xl font-bold ${
              c.tone === 'warning'
                ? 'text-status-warning'
                : c.tone === 'danger'
                  ? 'text-status-danger'
                  : 'text-fg-primary'
            }`}
          >
            {c.value}
          </div>
          {c.hint && (
            <div className="mt-1 text-[11px] text-fg-muted">{c.hint}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: NumbersTable
// ---------------------------------------------------------------------------

interface NumbersTableProps {
  rows: MessagingNumber[];
  sort: SortState;
  onSort: (key: SortKey) => void;
  expandedId: number | null;
  onToggleExpand: (id: number) => void;
  openKebabId: number | null;
  onOpenKebab: (id: number | null) => void;
  onTogglePause: (n: MessagingNumber) => void;
  onEditNotes: (n: MessagingNumber) => void;
  onRelease: (n: MessagingNumber) => void;
  setRowRef: (id: number) => (el: HTMLTableRowElement | null) => void;
}

function NumbersTable({
  rows,
  sort,
  onSort,
  expandedId,
  onToggleExpand,
  openKebabId,
  onOpenKebab,
  onTogglePause,
  onEditNotes,
  onRelease,
  setRowRef,
}: NumbersTableProps) {
  const columns: Array<{
    key: SortKey | 'actions';
    label: string;
    sortable: boolean;
    align?: 'left' | 'right';
  }> = [
    { key: 'phone_number', label: 'Number', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'sent_30d', label: 'Sent (30d)', sortable: true, align: 'right' },
    {
      key: 'delivered_rate_30d',
      label: 'Delivered %',
      sortable: true,
      align: 'right',
    },
    {
      key: 'opt_out_rate_30d',
      label: 'Opt-out %',
      sortable: true,
      align: 'right',
    },
    { key: 'last_used_at', label: 'Last used', sortable: true },
    { key: 'actions', label: '', sortable: false },
  ];

  return (
    <div className="bg-bg-raised rounded-lg border border-border-base overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-base bg-bg-base">
            {columns.map((c) => {
              const isActive = c.sortable && sort.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide ${
                    c.align === 'right' ? 'text-right' : 'text-left'
                  } ${
                    c.sortable
                      ? 'cursor-pointer hover:bg-bg-raised hover:text-fg-primary'
                      : ''
                  } ${isActive ? 'text-status-success' : 'text-fg-muted'}`}
                  onClick={
                    c.sortable ? () => onSort(c.key as SortKey) : undefined
                  }
                  aria-sort={
                    isActive
                      ? sort.order === 'desc'
                        ? 'descending'
                        : 'ascending'
                      : 'none'
                  }
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && isActive && (
                      <span aria-hidden="true">
                        {sort.order === 'desc' ? '↓' : '↑'}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((n) => {
            const isExpanded = expandedId === n.id;
            const isHighSent = (n.sent_30d ?? 0) > 10000;
            const delivWarn =
              n.delivered_rate_30d != null &&
              n.delivered_rate_30d < 0.9 &&
              (n.sent_30d ?? 0) > 0;
            const optWarn = (n.opt_out_rate_30d ?? 0) > 0.03;
            return (
              <React.Fragment key={n.id}>
                <tr
                  ref={setRowRef(n.id)}
                  className="border-b border-border-base hover:bg-bg-raised/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onToggleExpand(n.id)}
                      className="inline-flex items-center gap-1.5 font-mono text-fg-primary hover:text-accent-fg"
                      aria-expanded={isExpanded}
                      aria-label={`Toggle details for ${formatPhone(n.phone_number)}`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      {formatPhone(n.phone_number)}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={n.status} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span
                      className={
                        isHighSent ? 'font-bold text-fg-primary' : 'text-fg-secondary'
                      }
                    >
                      {(n.sent_30d ?? 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span
                      className={`inline-flex items-center gap-1 ${
                        delivWarn ? 'text-status-warning' : 'text-fg-secondary'
                      }`}
                    >
                      {delivWarn && (
                        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                      )}
                      {formatPct(n.delivered_rate_30d)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span
                      className={`inline-flex items-center gap-1 ${
                        optWarn ? 'text-status-danger' : 'text-fg-secondary'
                      }`}
                    >
                      {optWarn && (
                        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                      )}
                      {formatPct(n.opt_out_rate_30d)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fg-secondary whitespace-nowrap">
                    {relativeTime(n.last_used_at)}
                  </td>
                  <td className="px-4 py-3 text-right relative" data-kebab-root>
                    <button
                      type="button"
                      onClick={() =>
                        onOpenKebab(openKebabId === n.id ? null : n.id)
                      }
                      aria-label={`Actions for ${formatPhone(n.phone_number)}`}
                      aria-haspopup="menu"
                      aria-expanded={openKebabId === n.id}
                      className="inline-flex items-center justify-center w-9 h-9 rounded hover:bg-bg-elevated text-fg-secondary hover:text-fg-primary"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openKebabId === n.id && (
                      <div
                        role="menu"
                        className="absolute right-2 top-12 z-50 w-48 rounded-lg border border-border-base bg-bg-base shadow-2xl py-1"
                      >
                        {(n.status === 'active' || n.status === 'paused') && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => onTogglePause(n)}
                            className="w-full text-left px-3 py-2 text-sm text-fg-primary hover:bg-bg-raised"
                          >
                            {n.status === 'active' ? 'Pause' : 'Resume'}
                          </button>
                        )}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => onEditNotes(n)}
                          className="w-full text-left px-3 py-2 text-sm text-fg-primary hover:bg-bg-raised"
                        >
                          Edit notes
                        </button>
                        {n.status !== 'released' && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => onRelease(n)}
                            className="w-full text-left px-3 py-2 text-sm text-status-danger hover:bg-status-danger-bg/20"
                          >
                            Release…
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-bg-base/70 border-b border-border-base">
                    <td colSpan={columns.length} className="px-4 py-4">
                      <ExpandedRow number={n} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExpandedRow({ number: n }: { number: MessagingNumber }) {
  const caps = n.capabilities
    ? Object.entries(n.capabilities)
        .filter(([, v]) => v)
        .map(([k]) => k)
    : [];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-fg-muted font-semibold">
          Capabilities
        </div>
        <div className="text-fg-primary mt-0.5">
          {caps.length > 0 ? caps.join(', ') : '—'}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-fg-muted font-semibold">
          Monthly cost
        </div>
        <div className="text-fg-primary mt-0.5">
          {formatCents(n.monthly_cost_cents)}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-fg-muted font-semibold">
          Purchased
        </div>
        <div className="text-fg-primary mt-0.5">
          {formatDateTime(n.purchased_at)}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-fg-muted font-semibold">
          Area code
        </div>
        <div className="text-fg-primary mt-0.5">{n.area_code ?? '—'}</div>
      </div>
      <div className="sm:col-span-2 lg:col-span-4">
        <div className="text-[11px] uppercase tracking-wide text-fg-muted font-semibold">
          Notes
        </div>
        <div className="text-fg-secondary mt-0.5 whitespace-pre-wrap">
          {n.notes?.trim() ? n.notes : '—'}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Modal shell
// ---------------------------------------------------------------------------

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  widthClass?: string;
}

function Modal({
  title,
  onClose,
  children,
  widthClass = 'max-w-lg',
}: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className={`w-full ${widthClass} rounded-xl border border-border-base bg-bg-raised shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border-base">
          <h2 className="text-base font-semibold text-fg-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center w-9 h-9 rounded hover:bg-bg-elevated text-fg-secondary hover:text-fg-primary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: BuyNumberModal — two-step (Search → Confirm)
// ---------------------------------------------------------------------------

interface BuyNumberModalProps {
  onClose: () => void;
  onPurchased: (n: MessagingNumber) => void;
}

function BuyNumberModal({ onClose, onPurchased }: BuyNumberModalProps) {
  const [areaCode, setAreaCode] = useState('816');
  const [contains, setContains] = useState('');
  const [results, setResults] = useState<MessagingNumberSearchResult[] | null>(
    null,
  );
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [selected, setSelected] =
    useState<MessagingNumberSearchResult | null>(null);
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);

  const canSearch = /^\d{3}$/.test(areaCode);

  const doSearch = async () => {
    if (!canSearch) return;
    setSearching(true);
    setSearchError(null);
    setResults(null);
    try {
      const data = await searchAvailableNumbers({
        area_code: areaCode,
        contains: contains.trim() || undefined,
        limit: 10,
      });
      setResults(data);
    } catch (err) {
      setSearchError((err as Error)?.message ?? 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const doBuy = async () => {
    if (!selected) return;
    setBuying(true);
    setBuyError(null);
    try {
      const purchased = await buyMessagingNumber({
        phone_number: selected.phone_number,
      });
      onPurchased(purchased);
    } catch (err) {
      setBuyError((err as Error)?.message ?? 'Purchase failed.');
    } finally {
      setBuying(false);
    }
  };

  return (
    <Modal
      title={selected ? 'Confirm purchase' : 'Buy new number'}
      onClose={onClose}
      widthClass="max-w-2xl"
    >
      {!selected && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="sm:col-span-1 block">
              <span className="block text-xs uppercase tracking-wide text-fg-muted font-semibold mb-1">
                Area code
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{3}"
                maxLength={3}
                required
                value={areaCode}
                onChange={(e) =>
                  setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))
                }
                className="w-full rounded-lg border border-border-base bg-bg-base px-3 py-2 text-fg-primary font-mono focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="816"
              />
            </label>
            <label className="sm:col-span-2 block">
              <span className="block text-xs uppercase tracking-wide text-fg-muted font-semibold mb-1">
                Vanity contains (optional)
              </span>
              <input
                type="text"
                value={contains}
                onChange={(e) => setContains(e.target.value)}
                className="w-full rounded-lg border border-border-base bg-bg-base px-3 py-2 text-fg-primary focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Find numbers containing '4GOP', '2026', etc."
              />
            </label>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={doSearch}
              disabled={!canSearch || searching}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-3 py-2"
            >
              {searching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </button>
          </div>

          {searchError && (
            <div className="rounded-lg border border-status-danger-bg/40 bg-status-danger-bg/20 px-3 py-2 text-sm text-status-danger">
              {searchError}
            </div>
          )}

          {results && results.length === 0 && !searching && (
            <p className="text-sm text-fg-muted text-center py-4">
              No available numbers matched. Try a different area code or vanity
              pattern.
            </p>
          )}

          {results && results.length > 0 && (
            <div className="rounded-lg border border-border-base overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-bg-base">
                  <tr className="border-b border-border-base">
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wide text-fg-muted font-semibold">
                      Number
                    </th>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wide text-fg-muted font-semibold">
                      Locality / Region
                    </th>
                    <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-fg-muted font-semibold">
                      Monthly
                    </th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr
                      key={r.phone_number}
                      className="border-b border-border-base last:border-0"
                    >
                      <td className="px-3 py-2 font-mono text-fg-primary">
                        {formatPhone(r.phone_number)}
                      </td>
                      <td className="px-3 py-2 text-fg-secondary">
                        {[r.locality, r.region].filter(Boolean).join(', ') ||
                          '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-fg-secondary tabular-nums">
                        {formatCents(r.monthly_cost_cents)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setSelected(r)}
                          className="rounded bg-accent hover:bg-accent-hover text-white text-xs font-semibold px-3 py-1.5"
                        >
                          Buy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selected && (
        <div className="space-y-4">
          <p className="text-sm text-fg-primary leading-relaxed">
            You're about to buy{' '}
            <span className="font-mono font-semibold">
              {formatPhone(selected.phone_number)}
            </span>
            . Monthly cost:{' '}
            <span className="font-semibold">
              {formatCents(selected.monthly_cost_cents)}
            </span>
            . This number will be added to your Messaging Service pool and
            available for sends immediately.
          </p>

          {buyError && (
            <div className="rounded-lg border border-status-danger-bg/40 bg-status-danger-bg/20 px-3 py-2 text-sm text-status-danger">
              {buyError}
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setSelected(null)}
              disabled={buying}
              className="text-sm text-fg-secondary hover:text-fg-primary"
            >
              ← Back to results
            </button>
            <button
              type="button"
              onClick={doBuy}
              disabled={buying}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-semibold text-sm px-4 py-2"
            >
              {buying && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirm purchase
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: EditNotesModal
// ---------------------------------------------------------------------------

interface EditNotesModalProps {
  number: MessagingNumber;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

function EditNotesModal({
  number: n,
  onClose,
  onSaved,
  onError,
}: EditNotesModalProps) {
  const [notes, setNotes] = useState(n.notes ?? '');
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    setSaving(true);
    try {
      await updateMessagingNumber(n.id, { notes });
      onSaved();
    } catch (err) {
      onError((err as Error)?.message ?? 'Failed to save notes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit notes — ${formatPhone(n.phone_number)}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={5}
          autoFocus
          className="w-full rounded-lg border border-border-base bg-bg-base px-3 py-2 text-fg-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
          placeholder="Internal notes about this number (e.g. 'reserved for GOTV blast', 'carrier issue 2026-03-12')…"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-border-base bg-bg-base hover:bg-bg-elevated text-fg-primary text-sm font-semibold px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={doSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-semibold text-sm px-3 py-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save notes
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ReleaseModal — irreversible, guarded by last-4-digit typing
// ---------------------------------------------------------------------------

interface ReleaseModalProps {
  number: MessagingNumber;
  onClose: () => void;
  onReleased: () => void;
  onError: (msg: string) => void;
}

function ReleaseModal({
  number: n,
  onClose,
  onReleased,
  onError,
}: ReleaseModalProps) {
  const expected = last4(n.phone_number);
  const [typed, setTyped] = useState('');
  const [releasing, setReleasing] = useState(false);
  const matches = typed === expected;

  const doRelease = async () => {
    if (!matches) return;
    setReleasing(true);
    try {
      await releaseMessagingNumber(n.id);
      onReleased();
    } catch (err) {
      onError((err as Error)?.message ?? 'Release failed.');
    } finally {
      setReleasing(false);
    }
  };

  return (
    <Modal title={`Release ${formatPhone(n.phone_number)}?`} onClose={onClose}>
      <div className="space-y-4">
        <div className="rounded-lg border border-status-danger-bg/40 bg-status-danger-bg/15 p-3 text-sm text-fg-primary leading-relaxed">
          This will return the number to Twilio. Active conversations using this
          number will be orphaned.{' '}
          <strong className="text-status-danger">
            You cannot reclaim this specific number once released.
          </strong>
        </div>

        <div className="rounded-lg border border-border-base bg-bg-base p-3 text-sm text-fg-secondary">
          Sent{' '}
          <span className="font-semibold text-fg-primary">
            {(n.sent_30d ?? 0).toLocaleString()}
          </span>{' '}
          messages in the last 30 days.
          {n.last_used_at && (
            <>
              {' · '}Last used{' '}
              <span className="font-semibold text-fg-primary">
                {relativeTime(n.last_used_at)}
              </span>
            </>
          )}
        </div>

        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-fg-muted font-semibold mb-1">
            Type the last 4 digits to confirm:{' '}
            <span className="font-mono text-fg-secondary">{expected}</span>
          </span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/\D/g, '').slice(0, 4))}
            autoFocus
            className="w-full rounded-lg border border-border-base bg-bg-base px-3 py-2 text-fg-primary font-mono focus:outline-none focus:ring-2 focus:ring-status-danger"
            placeholder="••••"
            aria-invalid={typed.length === 4 && !matches}
          />
          {typed.length === 4 && !matches && (
            <span className="block mt-1 text-xs text-status-danger">
              Doesn't match. Re-check the last 4 digits of the number.
            </span>
          )}
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={releasing}
            className="rounded-lg border border-border-base bg-bg-base hover:bg-bg-elevated text-fg-primary text-sm font-semibold px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={doRelease}
            disabled={!matches || releasing}
            className="inline-flex items-center gap-1.5 rounded-lg bg-status-danger hover:bg-status-danger/90 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm px-3 py-2"
          >
            {releasing && <Loader2 className="w-4 h-4 animate-spin" />}
            Release
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default MessagingNumbersView;
